import { useCallback, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useFocusTransition } from '@/contexts/focusTransitionContext';
import {
  levelFocusApi,
  type LevelFocusResponse,
  type LevelStreamPair,
} from '@/services/levelFocusApi';
import { formatLevelStream } from '@/utils/levelStreamLabel';

import { useAuthenticatedCall } from './useAuthenticatedCall';

export interface UseLevelFocusResult {
  /** True only when active memberships expose more than one syllabus pair. */
  enabled: boolean;
  /** Initial focus/options fetch in flight. */
  isLoading: boolean;
  /** Server write in flight; dependent controls must remain blocked. */
  isUpdating: boolean;
  /** Stored focus. `null` means the student's own profile pair. */
  value: LevelStreamPair | null;
  /** Active-class-derived choices, own pair first. */
  options: LevelStreamPair[];
  /** The student's profile pair, or null until the first read resolves. */
  ownLevelStream: LevelStreamPair | null;
  /** Write the focus. Published only after the server accepts it. */
  setValue: (next: LevelStreamPair | null) => void;
}

export const LEVEL_FOCUS_QUERY_KEY = ['practice', 'focus-level'] as const;

/** Stable cache-key representation of one level/stream pair. */
export const levelFocusKey = (pair: LevelStreamPair): string =>
  `${pair.orgLevel}|${pair.examLevel}`;

/**
 * Reads and writes the student's durable syllabus focus.
 *
 * Read controllers never accept this pair as a query parameter. Consumers
 * put `levelFocusKey(value ?? ownLevelStream)` in their react-query key only;
 * the server reads the authoritative value from the User row. That cache slot
 * is enough to prevent a post-flip surface from repainting the previous
 * syllabus.
 */
export function useLevelFocus(): UseLevelFocusResult {
  const { isAuthenticated, user } = useAuth0();
  const { callWithAuth } = useAuthenticatedCall();
  const queryClient = useQueryClient();
  const transition = useFocusTransition();
  const retryMutationRef = useRef<(next: LevelStreamPair | null) => void>(
    () => undefined
  );

  // Per-user scoping prevents a warm cache crossing a sign-out/sign-in.
  const queryKey = useMemo(
    () => [...LEVEL_FOCUS_QUERY_KEY, user?.sub ?? null],
    [user?.sub]
  );

  const query = useQuery<LevelFocusResponse>({
    queryKey,
    enabled: isAuthenticated,
    // Membership cohorts change infrequently; page-to-page navigation should
    // share the accepted value instead of re-reading it.
    staleTime: 5 * 60 * 1000,
    queryFn: () => callWithAuth(token => levelFocusApi.get(token)),
  });

  const { mutate, isPending } = useMutation<
    { focusLevelStream: LevelStreamPair | null },
    unknown,
    LevelStreamPair | null,
    { transitionId: number }
  >({
    // Level and subject are two controls over one serving focus. Sharing the
    // scope guarantees their PATCHes can never overlap, including same-frame
    // programmatic calls before the disabled state paints.
    scope: { id: `student-focus:${user?.sub ?? 'anonymous'}` },
    // A focus GET that started before PATCH can otherwise publish its old
    // response after the write. Cancel this exact owner before writing.
    onMutate: async next => {
      const destination = next ?? query.data?.ownLevelStream ?? null;
      const transitionId = transition.begin({
        kind: 'level',
        destinationLabel: destination
          ? formatLevelStream({ ...destination, primaryStyle: 'compact' })
          : 'your level',
        target: {
          levelKey: destination ? levelFocusKey(destination) : null,
        },
        retry: () => retryMutationRef.current(next),
      });
      await queryClient.cancelQueries({ queryKey, exact: true });
      await queryClient.cancelQueries({
        queryKey: ['practice'],
        predicate: candidate =>
          !LEVEL_FOCUS_QUERY_KEY.every(
            (part, index) => candidate.queryKey[index] === part
          ),
      });
      return { transitionId };
    },
    mutationFn: next =>
      callWithAuth(token => levelFocusApi.update(next, token)),
    onSuccess: (data, _next, context) => {
      // A level decides whether the E/A-Math split applies. Invalidate every
      // cached applicability record before publishing the accepted pair; the
      // nested subject provider will resolve that record before replacement
      // content is allowed to fetch.
      void queryClient.invalidateQueries({
        queryKey: ['practice', 'focus-subject'],
        refetchType: 'none',
      });
      queryClient.setQueryData<LevelFocusResponse>(queryKey, previous =>
        previous
          ? { ...previous, focusLevelStream: data.focusLevelStream }
          : previous
      );
      // Every practice read is level-focus-sensitive. This deliberately also
      // refreshes focus-subject applicability when the chosen level changes.
      // The accepted focus-level row itself stays authoritative and excluded.
      void queryClient.invalidateQueries({
        queryKey: ['practice'],
        refetchType: 'none',
        predicate: candidate =>
          !LEVEL_FOCUS_QUERY_KEY.every(
            (part, index) => candidate.queryKey[index] === part
          ) &&
          !['practice', 'focus-subject'].every(
            (part, index) => candidate.queryKey[index] === part
          ),
      });
      transition.accept(context?.transitionId ?? transition.id);
    },
    // A rejection normally means membership changed. Keep the prior value and
    // refresh the exact options record before allowing another scoped write.
    onError: (error, _next, context) => {
      transition.failSave(context?.transitionId ?? transition.id, error);
      void queryClient.invalidateQueries({ queryKey, exact: true });
    },
  });

  retryMutationRef.current = mutate;

  const setValue = useCallback(
    (next: LevelStreamPair | null) => {
      mutate(next);
    },
    [mutate]
  );

  const options = query.data?.levelStreamOptions ?? [];
  const enabled = options.length > 1;

  return {
    enabled,
    isLoading: query.isLoading,
    isUpdating: isPending,
    value: enabled ? (query.data?.focusLevelStream ?? null) : null,
    options,
    ownLevelStream: query.data?.ownLevelStream ?? null,
    setValue,
  };
}
